import { useState } from 'react'
import { mockSalesforceUsers, PRICE_PER_AGENT, DEFAULT_SELECTED_AGENTS } from '../data/mockData'

// ── Icons ──────────────────────────────────────────────────────────────────
function CheckIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function SpinnerIcon() {
  return (
    <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}

function ChevronRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function ChevronLeft() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function ExternalLinkIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  )
}

// ── Step Indicator ─────────────────────────────────────────────────────────
const STEPS = [
  { number: 1, label: 'Basic Info' },
  { number: 2, label: 'Connect Stripe' },
  { number: 3, label: 'Assign Agents' },
  { number: 4, label: 'Auto-Match' },
  { number: 5, label: 'Done' },
]

function StepIndicator({ currentStep }) {
  return (
    <div className="flex items-center justify-center mb-10">
      {STEPS.map((step, idx) => (
        <div key={step.number} className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200 ${
              currentStep > step.number
                ? 'bg-blue-600 text-white'
                : currentStep === step.number
                ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                : 'bg-gray-100 text-gray-400'
            }`}>
              {currentStep > step.number ? <CheckIcon size={15} /> : step.number}
            </div>
            <span className={`text-xs mt-1.5 font-medium whitespace-nowrap ${
              currentStep >= step.number ? 'text-blue-600' : 'text-gray-400'
            }`}>
              {step.label}
            </span>
          </div>
          {idx < STEPS.length - 1 && (
            <div className={`w-12 md:w-16 h-px mb-5 mx-1 transition-colors duration-200 ${
              currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'
            }`} />
          )}
        </div>
      ))}
    </div>
  )
}

// ── Step 1: Basic Info ─────────────────────────────────────────────────────
function Step1BasicInfo({ formData, onChange }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Welcome to BillJean</h2>
        <p className="text-sm text-gray-500 mt-1">Let's get you set up. Confirm your info to get started.</p>
      </div>

      <div className="space-y-4 pt-1">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={e => onChange({ name: e.target.value })}
            className="input-base"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
          <input
            type="email"
            value={formData.email}
            onChange={e => onChange({ email: e.target.value })}
            className="input-base"
          />
          <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
            Pre-filled from your Salesforce session context
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Phone <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={e => onChange({ phone: e.target.value })}
            placeholder="+1 (555) 000-0000"
            className="input-base"
          />
        </div>
      </div>

      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-lg">
        <div className="mt-0.5 flex-shrink-0">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>
        <p className="text-sm text-blue-800 leading-relaxed">
          Prefer white-glove setup assistance?{' '}
          <a
            href="https://calendly.com"
            target="_blank"
            rel="noreferrer"
            className="font-semibold underline underline-offset-2 hover:text-blue-900 inline-flex items-center gap-1"
          >
            Book a complimentary 15-minute onboarding call <ExternalLinkIcon />
          </a>
          .
        </p>
      </div>
    </div>
  )
}

// ── Step 2: Connect Stripe ─────────────────────────────────────────────────
function StripeLogoMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="6" fill="white" fillOpacity="0.2" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.45 11.6c0-.93.76-1.29 2.02-1.29 1.8 0 4.09.55 5.89 1.52V6.43C21.47 5.53 19.08 5 15.47 5 10.3 5 6.8 7.62 6.8 12.04c0 6.89 9.49 5.79 9.49 8.77 0 1.1-.96 1.46-2.29 1.46-1.98 0-4.52-.82-6.52-1.93v5.47c2.22.96 4.47 1.36 6.52 1.36 4.96 0 8.73-2.46 8.73-6.96-.03-7.44-9.28-6.13-9.28-8.61z"
        fill="white"
      />
    </svg>
  )
}

function Step2ConnectStripe({ formData, onChange }) {
  const [connecting, setConnecting] = useState(false)

  const handleConnect = () => {
    setConnecting(true)
    setTimeout(() => {
      setConnecting(false)
      onChange({ stripeConnected: true })
    }, 2200)
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Connect Your Stripe Account</h2>
        <p className="text-sm text-gray-500 mt-1">
          Authorize BillJean to securely read your Stripe payment data. You can revoke access from Stripe at any time.
        </p>
      </div>

      <div className="flex flex-col items-center py-8 gap-6">
        {!formData.stripeConnected ? (
          <>
            <div className="flex items-center gap-4 text-gray-400">
              <div className="flex flex-col items-center gap-1.5">
                <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
                  <span className="text-white text-sm font-bold">BJ</span>
                </div>
                <span className="text-xs text-gray-500 font-medium">BillJean</span>
              </div>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="flex flex-col items-center gap-1.5">
                <div className="w-12 h-12 rounded-xl bg-[#635BFF] flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M15.45 11.6c0-.93.76-1.29 2.02-1.29 1.8 0 4.09.55 5.89 1.52V6.43C21.47 5.53 19.08 5 15.47 5 10.3 5 6.8 7.62 6.8 12.04c0 6.89 9.49 5.79 9.49 8.77 0 1.1-.96 1.46-2.29 1.46-1.98 0-4.52-.82-6.52-1.93v5.47c2.22.96 4.47 1.36 6.52 1.36 4.96 0 8.73-2.46 8.73-6.96-.03-7.44-9.28-6.13-9.28-8.61z" fill="white"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-500 font-medium">Stripe</span>
              </div>
            </div>

            <button
              onClick={handleConnect}
              disabled={connecting}
              className="inline-flex items-center gap-3 px-6 py-3 bg-[#635BFF] hover:bg-[#5851EB] text-white font-semibold rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed shadow-sm"
            >
              {connecting ? (
                <>
                  <SpinnerIcon />
                  Connecting to Stripe...
                </>
              ) : (
                <>
                  <StripeLogoMark />
                  Connect with Stripe
                </>
              )}
            </button>

            <p className="text-xs text-gray-400 text-center max-w-xs leading-relaxed">
              You'll be redirected to Stripe's secure authorization page. BillJean only reads payment data — it cannot create charges.
            </p>
          </>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900">Stripe connected</div>
              <div className="text-sm text-gray-500 mt-0.5">
                Account: <span className="font-medium text-gray-700">Acme Corp</span>
              </div>
              <div className="text-xs text-gray-400 mt-0.5 font-mono">acct_1NdvfQDKoZ1Cm7p</div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-100 rounded-lg">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              <span className="text-sm text-green-700 font-medium">Read and write access granted</span>
            </div>
            <button
              onClick={() => onChange({ stripeConnected: false })}
              className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2"
            >
              Use a different account
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Step 3: Assign Agents ──────────────────────────────────────────────────
function Step3AssignAgents({ formData, onChange }) {
  const [search, setSearch] = useState('')

  const selectedSet = new Set(formData.selectedAgents)

  const toggle = (id) => {
    const next = new Set(selectedSet)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange({ selectedAgents: Array.from(next) })
  }

  const selectAll = () => onChange({ selectedAgents: mockSalesforceUsers.map(u => u.id) })
  const clearAll  = () => onChange({ selectedAgents: [] })

  const filteredUsers = search
    ? mockSalesforceUsers.filter(u => {
        const q = search.toLowerCase()
        return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.role.toLowerCase().includes(q)
      })
    : mockSalesforceUsers

  const count = selectedSet.size
  const monthlyCost = count * PRICE_PER_AGENT

  const roleColors = {
    'Account Executive': 'bg-blue-50 text-blue-700',
    'Sales Rep':         'bg-purple-50 text-purple-700',
    'SDR':               'bg-amber-50 text-amber-700',
    'Senior AE':         'bg-blue-50 text-blue-700',
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Assign Agent Access</h2>
        <p className="text-sm text-gray-500 mt-1">
          Select which team members can see Stripe payment data in Salesforce.
        </p>
      </div>

      {/* Cost preview */}
      <div className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
        count > 0 ? 'bg-blue-50 border-blue-100' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={count > 0 ? '#2563EB' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <span className={`text-sm font-medium ${count > 0 ? 'text-blue-800' : 'text-gray-400'}`}>
            {count === 0 ? 'No agents selected' : `${count} agent${count !== 1 ? 's' : ''} selected`}
          </span>
        </div>
        {count > 0 && (
          <div className="text-right">
            <div className="text-lg font-bold text-blue-700">${monthlyCost}<span className="text-sm font-normal">/mo</span></div>
            <div className="text-xs text-blue-500">$9/agent</div>
          </div>
        )}
      </div>

      {/* Search + filter toolbar */}
      <div className="space-y-2">
        <div className="relative">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
          />
        </div>

        <div className="flex items-center justify-end gap-2 text-xs">
          <button onClick={selectAll} className="text-blue-500 hover:text-blue-700 font-medium">Select all</button>
          <span className="text-gray-300">·</span>
          <button onClick={clearAll} className="text-gray-400 hover:text-gray-600">Clear</button>
        </div>

        {search && (
          <p className="text-xs text-gray-400">
            Showing {filteredUsers.length} of {mockSalesforceUsers.length} users
          </p>
        )}
      </div>

      {/* User list */}
      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-400">No users match your search.</div>
        ) : filteredUsers.map(user => {
          const isSelected = selectedSet.has(user.id)
          return (
            <label
              key={user.id}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                isSelected ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggle(user.id)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
              />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                isSelected ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {user.initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">{user.name}</div>
                <div className="text-xs text-gray-400 truncate">{user.email}</div>
              </div>
              <span className={`badge text-xs flex-shrink-0 ${roleColors[user.role] || 'bg-gray-100 text-gray-600'}`}>
                {user.role}
              </span>
            </label>
          )
        })}
      </div>

      <p className="text-xs text-gray-400 flex items-start gap-1.5 pt-1">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 flex-shrink-0"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
        You'll be billed $9/mo per agent with access. You can add or remove agents at any time from settings.
      </p>
    </div>
  )
}

// ── Step 4: Auto-Match ─────────────────────────────────────────────────────
function Step4AutoMatch({ formData, onChange, onNavigate }) {
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)

  const handleRunMatch = () => {
    setLoading(true)
    setProgress(0)
    const steps = [10, 25, 45, 60, 75, 88, 100]
    steps.forEach((p, i) => {
      setTimeout(() => {
        setProgress(p)
        if (p === 100) {
          setTimeout(() => {
            setLoading(false)
            onChange({ autoMatchRun: true, matchResults: { high: 142, review: 8, unmatched: 7 } })
          }, 400)
        }
      }, i * 420)
    })
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Auto-Match Accounts</h2>
        <p className="text-sm text-gray-500 mt-1">
          We'll scan your Salesforce Accounts and attempt to match each one to a Stripe Customer using email domains, company names, and website domains.
        </p>
      </div>

      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Matching algorithm priority</p>
        {[
          { label: 'Email domain match', detail: 'Contact email domains vs Stripe customer domains', color: 'bg-blue-500' },
          { label: 'Contact email match', detail: 'Direct email address matches', color: 'bg-blue-400' },
          { label: 'Company name similarity', detail: '85%+ fuzzy match on normalized names', color: 'bg-blue-300' },
          { label: 'Website domain match', detail: 'Root domain comparison', color: 'bg-blue-200' },
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <div className={`w-1.5 h-1.5 rounded-full ${item.color} mt-1.5 flex-shrink-0`} />
            <div>
              <span className="text-sm font-medium text-gray-700">{item.label}</span>
              <span className="text-xs text-gray-400 ml-2">{item.detail}</span>
            </div>
          </div>
        ))}
      </div>

      {!formData.autoMatchRun && !loading && (
        <div className="flex justify-center pt-2">
          <button
            onClick={handleRunMatch}
            className="btn-primary px-8 py-3 text-base"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            Run Auto-Match
          </button>
        </div>
      )}

      {loading && (
        <div className="space-y-3 py-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 font-medium">Scanning accounts...</span>
            <span className="text-gray-400">{progress}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-400">
            {progress < 40 ? 'Fetching Salesforce accounts...' : progress < 70 ? 'Comparing against Stripe customers...' : 'Calculating confidence scores...'}
          </p>
        </div>
      )}

      {formData.autoMatchRun && formData.matchResults && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-green-700 mb-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span className="text-sm font-semibold">Auto-match complete</span>
          </div>

          <button
            onClick={() => {}}
            className="w-full flex items-center justify-between p-4 bg-green-50 border border-green-100 rounded-xl hover:bg-green-100 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 border border-green-200 flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div>
                <div className="font-semibold text-green-900">{formData.matchResults.high} accounts matched</div>
                <div className="text-xs text-green-600">High confidence — no action needed</div>
              </div>
            </div>
            <span className="badge bg-green-100 text-green-700">Auto-confirmed</span>
          </button>

          <button
            onClick={() => onNavigate('matching')}
            className="w-full flex items-center justify-between p-4 bg-amber-50 border border-amber-100 rounded-xl hover:bg-amber-100 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </div>
              <div>
                <div className="font-semibold text-amber-900">{formData.matchResults.review} accounts need review</div>
                <div className="text-xs text-amber-600">Lower confidence — your approval needed</div>
              </div>
            </div>
            <span className="text-amber-600 text-xs font-medium flex items-center gap-1">
              Review <ChevronRight />
            </span>
          </button>

          <button
            onClick={() => onNavigate('matching')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              </div>
              <div>
                <div className="font-semibold text-gray-700">{formData.matchResults.unmatched} accounts unmatched</div>
                <div className="text-xs text-gray-400">No Stripe customer found — link manually</div>
              </div>
            </div>
            <span className="text-gray-500 text-xs font-medium flex items-center gap-1">
              Link <ChevronRight />
            </span>
          </button>
        </div>
      )}
    </div>
  )
}

// ── Step 5: Done ───────────────────────────────────────────────────────────
function Step5Done({ formData, onNavigate, onRestart }) {
  const agentCount = formData.selectedAgents.length
  const monthlyCost = agentCount * PRICE_PER_AGENT

  return (
    <div className="space-y-6 text-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">You're all set!</h2>
          <p className="text-gray-500 mt-1">BillJean is configured and ready to go.</p>
        </div>
      </div>

      <div className="card p-5 text-left space-y-4">
        <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Setup summary</p>

        <div className="space-y-3">
          <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#635BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
              </svg>
              <span className="text-sm text-gray-700">Stripe account</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-gray-900">Acme Corp</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          </div>

          <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <span className="text-sm text-gray-700">Agents with access</span>
            </div>
            <span className="text-sm font-medium text-gray-900">{agentCount} agents · ${monthlyCost}/mo</span>
          </div>

          <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
              <span className="text-sm text-gray-700">Auto-matched accounts</span>
            </div>
            <span className="text-sm font-medium text-gray-900">142 confirmed</span>
          </div>

          <div className="flex items-center justify-between py-2.5">
            <div className="flex items-center gap-2.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span className="text-sm text-gray-700">Pending review</span>
            </div>
            <span className="text-sm font-medium text-amber-600">8 need attention</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button
          onClick={() => onNavigate('dashboard')}
          className="btn-primary flex-1 justify-center py-2.5"
        >
          Go to Dashboard
          <ChevronRight />
        </button>
        <button
          onClick={onRestart}
          className="btn-secondary flex-1 justify-center py-2.5"
        >
          Restart Demo
        </button>
      </div>
    </div>
  )
}

// ── Setup Wizard (orchestrator) ────────────────────────────────────────────
export default function SetupWizard({ onNavigate }) {
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    name: 'Alex Rivera',
    email: 'alex.rivera@acmecorp.com',
    phone: '',
    stripeConnected: false,
    selectedAgents: [...DEFAULT_SELECTED_AGENTS],
    autoMatchRun: false,
    matchResults: null,
  })

  const update = (changes) => setFormData(prev => ({ ...prev, ...changes }))

  const canNext = () => {
    if (currentStep === 2 && !formData.stripeConnected) return false
    return true
  }

  const handleNext = () => setCurrentStep(s => Math.min(s + 1, 5))
  const handleBack = () => setCurrentStep(s => Math.max(s - 1, 1))
  const handleRestart = () => {
    setCurrentStep(1)
    setFormData({
      name: 'Alex Rivera',
      email: 'alex.rivera@acmecorp.com',
      phone: '',
      stripeConnected: false,
      selectedAgents: [...DEFAULT_SELECTED_AGENTS],
      autoMatchRun: false,
      matchResults: null,
    })
  }

  return (
    <div className="min-h-full bg-gray-50 px-4 py-10">
      {/* Page header */}
      <div className="max-w-2xl mx-auto mb-8">
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
          <span>BillJean</span>
          <span>›</span>
          <span>Setup Wizard</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Getting Started</h1>
        <p className="text-sm text-gray-500 mt-1">Complete each step to finish configuring BillJean.</p>
      </div>

      <div className="max-w-2xl mx-auto">
        <StepIndicator currentStep={currentStep} />

        {/* Step card */}
        <div className="card p-8">
          {currentStep === 1 && <Step1BasicInfo formData={formData} onChange={update} />}
          {currentStep === 2 && <Step2ConnectStripe formData={formData} onChange={update} />}
          {currentStep === 3 && <Step3AssignAgents formData={formData} onChange={update} />}
          {currentStep === 4 && <Step4AutoMatch formData={formData} onChange={update} onNavigate={onNavigate} />}
          {currentStep === 5 && <Step5Done formData={formData} onNavigate={onNavigate} onRestart={handleRestart} />}

          {/* Navigation */}
          {currentStep < 5 && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className="btn-secondary"
              >
                <ChevronLeft />
                Back
              </button>

              <span className="text-xs text-gray-400">Step {currentStep} of {STEPS.length}</span>

              {currentStep === 4 ? (
                <button
                  onClick={handleNext}
                  disabled={false}
                  className="btn-primary"
                >
                  Finish Setup
                  <ChevronRight />
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={!canNext()}
                  className="btn-primary"
                >
                  Next
                  <ChevronRight />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
